import type { AuthProfile } from "@/contexts/AuthContext"
import { syncOneSignalSession } from "@/services/notifications/oneSignalClient"
import { getSupabaseClient } from "@/services/supabaseClient"

export type NotificationPreferences = {
  push: boolean
  email: boolean
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asBoolean(value: unknown, fallback: boolean) {
  return typeof value === "boolean" ? value : fallback
}

export function getNotificationPreferences(profile: AuthProfile | null): NotificationPreferences {
  const settings = asRecord(profile?.settings)
  const notifications = asRecord(settings?.notifications)

  return {
    push: asBoolean(notifications?.push, false),
    email: asBoolean(notifications?.email, true),
  }
}

export async function updateNotificationPreferences(input: {
  profile: AuthProfile | null
  preferences: NotificationPreferences
}) {
  const client = getSupabaseClient()
  if (!client) throw new Error("Supabase is not configured for this build.")

  const { data: userResult, error: userError } = await client.auth.getUser()
  if (userError || !userResult.user) throw new Error("Sign in before changing notifications.")

  const settings = asRecord(input.profile?.settings) ?? {}
  const nextSettings = {
    ...settings,
    notifications: input.preferences,
  }

  const { error } = await client
    .from("profiles")
    .update({ settings: nextSettings })
    .eq("id", userResult.user.id)

  if (error) throw error

  if (input.preferences.push) {
    await syncOneSignalSession({ id: userResult.user.id })
  } else {
    await syncOneSignalSession(null)
  }
}
